import React from 'react';
import { Rnd, Props as RndProps } from 'react-rnd';

interface ChatPosition {
	x: number;
	y: number;
}

interface ChatSize {
	width: number;
	height: number;
}

const menuRight = 10;
const menuTop = 20;
const menuHeight = 50;

const initialSize = {
	width: Math.min(500, window.innerWidth * 0.9),
	height: Math.min(800, window.innerHeight * 0.8),
};

const calculatePosition = (size: ChatSize): ChatPosition => {
	const calculatedX = window.innerWidth - size.width - menuRight;
	const calculatedY = menuTop + menuHeight;
	return {
		x: Math.max(0, calculatedX),
		y: Math.max(0, calculatedY),
	};
};
export const useChatPropsAndPosition = () => {
	const rndRef = React.useRef<Rnd>(null);
	const initialPosition = calculatePosition(initialSize);
	const [position, setPosition] = React.useState<ChatPosition>(initialPosition);
	const [size, setSize] = React.useState<ChatSize>(initialSize);

	React.useEffect(() => {
		const { x, y } = calculatePosition(size);
		setPosition({ x, y });

		if (rndRef.current) {
			rndRef.current.updatePosition({ x, y });
		}
	}, [size]);

	React.useLayoutEffect(() => {
		const handleMoveIntoView = () => {
			if (rndRef.current) {
				const chatWidth = size.width;
				const chatHeight = size.height;

				const calculatedX = Math.min(
					window.innerWidth - chatWidth - menuRight,
					window.innerWidth - chatWidth,
				);

				const calculatedY = menuTop + menuHeight;
				const newX = Math.max(0, calculatedX);
				const newY = Math.max(0, calculatedY);

				rndRef.current.updatePosition({ x: newX, y: newY });
				setSize({ width: chatWidth, height: chatHeight });
			}
		};

		window.addEventListener('resize', handleMoveIntoView);
		return () => window.removeEventListener('resize', handleMoveIntoView);
	}, [size.height, size.width]);

	const handleResizeStyle: React.CSSProperties = { width: 25, height: 25 };

	const rndProps: RndProps = {
		ref: rndRef,
		default: {
			x: position.x,
			y: position.y,
			width: size.width,
			height: size.height,
		},
		minWidth: 300,
		minHeight: 400,
		size,
		onResize: (_e, _direction, ref) => {
			setSize({
				width: ref.offsetWidth,
				height: ref.offsetHeight,
			});
		},
		dragHandleClassName: 'drag-handle',
		bounds: 'body',
		enableResizing: {
			top: true,
			right: true,
			bottom: true,
			left: true,
			topRight: true,
			bottomRight: true,
			bottomLeft: true,
			topLeft: true,
		},
		resizeHandleStyles: {
			topRight: handleResizeStyle,
			bottomRight: handleResizeStyle,
			bottomLeft: handleResizeStyle,
			topLeft: handleResizeStyle,
		},
	};

	return { rndProps };
};
